import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { FiExternalLink, FiImage } from "react-icons/fi";
import { resolveStorageUrl } from "../../Helper/storage";
import { PRODUCTION_SITE_URL, buildPublicUrl } from "../../Helper/siteLinks";

const LIMIT = 4;

type ListItem = { id?: string; contentType?: string; language?: string; slug?: string; path?: string; title?: string; updatedAt?: string };

type Article = {
	id: string;
	contentType: string;
	title: string;
	summary: string;
	image: string;
	type: string;
	date: string;
	path: string;
};

const isHexId = (s: string) => /^[a-f0-9]{24}$/i.test(s);
const titleCase = (s: string) => s.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/** Human label for a content item : its type, or the path's leading segment for hex-id dynamic types. */
const typeLabel = (item: ListItem): string => {
	const ct = (item.contentType ?? "").trim();
	if (ct && !isHexId(ct)) return titleCase(ct);
	const seg = (item.path ?? "").split("/").filter(Boolean)[0];
	return seg ? titleCase(seg) : "Contenu";
};

const frDate = (iso?: string): string => {
	if (!iso) return "";
	try {
		return new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short", year: "numeric" }).format(new Date(iso));
	} catch {
		return "";
	}
};

/** Pull the illustration key out of a content item's `image` field (a {src,options} object or a bare string). */
const imageKey = (data: Record<string, any>): string => {
	const raw = data?.image ?? data?.imageSrc ?? data?.cover ?? data?.heroImage;
	if (!raw) return "";
	if (typeof raw === "string") return raw;
	if (typeof raw === "object" && typeof raw.src === "string") return raw.src;
	return "";
};

const useLatestArticles = () => {
	const [articles, setArticles] = useState<Article[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				// Latest ARTICLES only (contentType=article) : the home shows editorial news, not the
				// most-recently-touched record of any type (e.g. a taux/gouverneur edit). includeLinks=false:
				// the cards don't show deploy links, so skip the (heavy) deployed_files index build the
				// Content list needs : keeps the dashboard fast.
				const listRes = await axios.get<{ items?: ListItem[] }>("/api/content/list", { params: { page: 1, pageSize: LIMIT, contentType: "article", includeLinks: false }, timeout: 25000 });
				const items = (listRes.data?.items ?? []).slice(0, LIMIT);
				const detailed = await Promise.all(
					items.map(async (item): Promise<Article> => {
						const base: Article = {
							id: item.id ?? "",
							contentType: item.contentType ?? "",
							title: item.title ?? item.slug ?? "Sans titre",
							summary: "",
							image: "",
							type: typeLabel(item),
							date: frDate(item.updatedAt),
							path: item.path ?? ""
						};
						try {
							const lang = item.language ?? "fr";
							const res = await axios.get<any>(`/api/content/${encodeURIComponent(item.contentType ?? "")}/${lang}`, {
								params: { slug: item.slug ?? item.id ?? "" },
								timeout: 25000
							});
							const data = res.data?.current?.data ?? res.data?.data ?? {};
							return {
								...base,
								title: data.title || base.title,
								summary: typeof data.summary === "string" ? data.summary : typeof data.description === "string" ? data.description : "",
								image: resolveStorageUrl(imageKey(data)),
								date: frDate(data["date-publication"] || item.updatedAt),
								path: data.path || base.path
							};
						} catch {
							return base;
						}
					})
				);
				if (!cancelled) setArticles(detailed);
			} catch {
				if (!cancelled) setArticles([]);
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	return { articles, loading };
};

const editLink = (a: Article): string => (a.contentType && a.id ? `/content/${a.contentType}/${a.id}` : "/pages");

const Card: React.FC<{ article: Article }> = ({ article }) => {
	const [imgFailed, setImgFailed] = useState(false);
	const showImage = article.image && !imgFailed;
	return (
	<div className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white">
		<Link to={editLink(article)} className="block">
			{showImage ? (
				<img src={article.image} alt={article.title} className="h-36 w-full object-cover" loading="lazy" onError={() => setImgFailed(true)} />
			) : (
				<div className="flex h-36 w-full items-center justify-center bg-slate-100 text-slate-300">
					<FiImage className="h-7 w-7" />
				</div>
			)}
		</Link>
		<div className="flex flex-1 flex-col p-4">
			<div className="flex items-center gap-2 text-[11px] text-slate-400">
				<span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-500">{article.type}</span>
				{article.date && <span>{article.date}</span>}
			</div>
			<Link
				to={editLink(article)}
				className="mt-1.5 block overflow-hidden text-sm font-bold leading-snug text-slate-900 hover:text-sky-700 [-webkit-box-orient:vertical] [-webkit-line-clamp:2] [display:-webkit-box]"
			>
				{article.title}
			</Link>
			{article.summary && (
				<p className="mt-1.5 overflow-hidden text-xs leading-relaxed text-slate-500 [-webkit-box-orient:vertical] [-webkit-line-clamp:3] [display:-webkit-box]">
					{article.summary}
				</p>
			)}
			{article.path && (
				<a
					href={buildPublicUrl(PRODUCTION_SITE_URL, article.path)}
					target="_blank"
					rel="noreferrer"
					className="mt-auto inline-flex items-center gap-1 pt-3 text-[11px] font-semibold text-sky-600 hover:text-sky-700 hover:underline"
				>
					Voir en ligne <FiExternalLink className="h-3 w-3" />
				</a>
			)}
		</div>
	</div>
	);
};

/** The four most recent articles, each as a card with its illustration + summary. */
export const ArticleCards: React.FC = () => {
	const { articles, loading } = useLatestArticles();

	if (loading) {
		return (
			<div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
				{Array.from({ length: LIMIT }).map((_, i) => (
					<div key={`ac-skel-${i}`} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
						<div className="h-36 w-full animate-pulse bg-slate-100" />
						<div className="space-y-2 p-4">
							<div className="h-3 w-1/3 animate-pulse rounded bg-slate-100" />
							<div className="h-4 w-5/6 animate-pulse rounded bg-slate-100" />
							<div className="h-3 w-full animate-pulse rounded bg-slate-100" />
							<div className="h-3 w-2/3 animate-pulse rounded bg-slate-100" />
						</div>
					</div>
				))}
			</div>
		);
	}

	if (articles.length === 0) return null;

	return (
		<div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
			{articles.map((a, i) => (
				<Card key={a.id || `ac-${i}`} article={a} />
			))}
		</div>
	);
};

export default ArticleCards;
